import React, { useEffect } from "react";
import { PremiumFilled } from "@wix/wix-ui-icons-common/lazy";
import {
  Box,
  Button,
  Card,
  MarketingPageLayout,
  MarketingPageLayoutContent,
  Page,
  Loader,
  SkeletonGroup,
  SkeletonRectangle,
  ToggleSwitch,
  FormField,
  Text,
} from "@wix/design-system";
import { dashboard } from "@wix/dashboard";
import { createClient } from "@wix/sdk";
import { ReactTagManager } from "react-gtm-ts";
import { useTranslation } from "react-i18next";
import { initAnalytics, track } from "./analytics";
import { embeddedScripts, appInstances } from "@wix/app-management";
import { WdsProviders } from "./components/wds-providers";


// Embedded Script Toggle Component
// This component manages the state of embedded scripts using the Wix App Management API
// It provides a toggle switch to enable/disable embedded scripts on the site
const EmbeddedScriptToggle = ({ client }: { client: any }) => {
  const { t } = useTranslation();
  const [isEnabled, setIsEnabled] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  // Get the current embedded script state
  // Uses embeddedScripts.getEmbeddedScript() to retrieve the current state
  const getEmbeddedScriptState = async () => {
    try {
      if (!client) return;
      
      const response = await client.embeddedScripts.getEmbeddedScript({
        // componentId: "your-component-id" // Replace with actual component ID from your app dashboard
      });
      
      // The disabled flag is inverted for the UI (true = disabled, false = enabled)
      setIsEnabled(!response.disabled);
      setIsLoading(false);
    } catch (err: any) {
      if (err.code === 404) {
        // No embedded script exists yet, default to disabled
        setIsEnabled(false);
        setIsLoading(false);
      } else {
        setError(err.message || "Failed to get embedded script state");
        setIsLoading(false);
      }
    }
  };

  // Update embedded script state
  // Uses embeddedScripts.embedScript() to enable/disable the script
  const updateEmbeddedScript = async (enabled: boolean) => {
    try {
      setIsLoading(true);
      setError(null);
      
      await client.embeddedScripts.embedScript(
        {
          disabled: !enabled, // Invert for API (true = disabled, false = enabled)
          parameters: {
            isEnabled: enabled.toString(),
          } // Add any script parameters here as needed
        },
        {
          // componentId: "your-component-id" // Replace with actual component ID from your app dashboard
        }
      );
      
      setIsEnabled(enabled);
      setIsLoading(false);
    } catch (err: any) {
      setError(err.message || "Failed to update embedded script");
      setIsLoading(false);
    }
  };

  // Handle toggle change
  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateEmbeddedScript(event.target.checked);
  };

  // Load initial state
  React.useEffect(() => {
    if (client) {
      getEmbeddedScriptState();
    }
  }, [client, getEmbeddedScriptState]);

  if (isLoading) {
    return (
      <Box direction="vertical" gap="SP2">
        <Text size="small">{t("baw-loading-settings")}</Text>
        <Loader size="tiny" />
      </Box>
    );
  }

  return (
    <Box direction="vertical" gap="SP2">
      <FormField
        label={t("baw-toggle-label")}
        labelPlacement="left"
        stretchContent={false}
      >
        <ToggleSwitch
          checked={isEnabled}
          onChange={handleToggleChange}
          disabled={isLoading}
        />
      </FormField>
      {/* {error && (
        <Text size="small" color="destructive">
          {error}
        </Text>
      )} */}
      {/* <Text size="small" secondary>
        {isEnabled 
          ? "Embedded script is currently active on your site."
          : "Embedded script is currently disabled."
        }
      </Text> */}
    </Box>
  );
};

function inIframe() {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

const tagManagerArgs = {
  code: "GTM-WZQPMC7",
};

function App() {
  const { t } = useTranslation();
  const appId = "6dbe5cd4-73c1-406b-97b1-2aa351d4416e"
  const [client, setClient] = React.useState(null as any);
  // Hard code the message content for black and white site functionality
  const [message, setMessage] = React.useState({
    _id: "black-white-site",
    title: "",
    description: "",
    image: null,
    redirectUrl: "https://www.wix.com"
  } as any);
  const token = new URLSearchParams(window.location.search).get("token");
  const [isUpgraded, setIsUpgraded] = React.useState(false);
  const [instanceData, setInstanceData] = React.useState({
    instance: {
      isFree: true,
      availablePlans: [],
      instanceId: "",
    },
  } as any);

  const URLS = {
    editorUrl: `https://www.wix.com/editor/${instanceData?.site?.siteId}`,
    upgradeUrl: `https://memberships.certifiedcode.us/${window.location.search}`,
  };

  useEffect(() => {
    ReactTagManager.init(tagManagerArgs);
    initAnalytics();
    try {
      if (inIframe()) {
        var c = createClient({
          host: dashboard.host(),
          auth: dashboard.auth(),
          modules: {
            dashboard,
            embeddedScripts,
            appInstances,
          },
        });
        setClient(c);
      }
    } catch (error) {
      throw error;
    }
    
    // Get app instance data using Wix API
    if (client) {
      client.appInstances.getAppInstance()
        .then((data: any) => {
          setInstanceData(data);
          setIsUpgraded(data?.instance?.isFree === false);
        })
        .catch((error: any) => {
          console.error("Error getting app instance:", error);
        });
    }
    // // Check if the iframe has already been loaded on this page session
    // if (sessionStorage.getItem("iframeLoaded")) {
    //   // Hide the current iframe by setting display to none
    //   document.body.style.display = "none"; // Hide the entire body of the iframe content
    // } else {
    //   // Mark the iframe as loaded
    //   sessionStorage.setItem("iframeLoaded", "true");
    // }
    // // clean when unload
    // window.addEventListener("beforeunload", () => {
    //   sessionStorage.removeItem("iframeLoaded");
    // });
  }, []);

  useEffect(() => {
    if (token && message?.redirectUrl) {
      window.location.href = `https://www.wix.com/installer/install?token=${token}&appId=${appId}&redirectUrl=${message.redirectUrl}`;
    }
  }, [token, message]);

  if (token) {
    return (
      <WdsProviders>
        <Page height="100vh">
          <Page.Content>
            <Box height={"90vh"} direction="vertical" verticalAlign="middle">
              <Loader text={t("loading")} />
            </Box>
          </Page.Content>
        </Page>
      </WdsProviders>
    );
  }

  return (
    <WdsProviders>
      <Card>
        <MarketingPageLayout
          removeImageHorizontalPadding
          removeImageVerticalPadding
          content={
            <Box height="840px" verticalAlign="middle">
              {message._id ? (
                <MarketingPageLayoutContent
                  title={t("baw-title")}
                  content={t("baw-content")}
                  actions={
                    <Box gap="SP2" direction="vertical">
                      <Button
                        suffixIcon={<PremiumFilled />}
                        skin={isUpgraded ? "premium-light" : "premium"}
                        as="a"
                        href={URLS.upgradeUrl}
                        target="_blank"
                        onClick={() => track("upgrade_clicked")}
                      >
                        {isUpgraded ? t("manage-plan") : t("upgrade")}
                      </Button>
                      {/* <Box gap="SP2">
                        <Button
                          suffixIcon={<Edit />}
                          disabled={!instanceData?.site?.siteId}
                          as="a"
                          href={URLS.editorUrl}
                          target="_blank"
                        >
                          {"Edit Site"}
                        </Button>
                        <Button
                          suffixIcon={<PremiumFilled />}
                          disabled={!instanceData?.instanceId}
                          skin={isUpgraded ? "premium-light" : "premium"}
                          as="a"
                          href={URLS.upgradeUrl}
                          target="_blank"
                        >
                          {isUpgraded ? "Manage Plan" : "Upgrade to set live"}
                        </Button>
                      </Box> */}
                      {client && (
                        <EmbeddedScriptToggle client={client} />
                      )}
                    </Box>
                  }
                />
              ) : (
                <MarketingPageLayoutContent
                  title={
                    <SkeletonGroup skin="light">
                      <Box gap="SP2" direction="vertical">
                        <SkeletonRectangle height={"72px"} width={"396px"} />
                      </Box>
                    </SkeletonGroup>
                  }
                  content={
                    <SkeletonGroup skin="light">
                      <Box gap="SP2" direction="vertical">
                        <SkeletonRectangle height={"48px"} width={"396px"} />
                      </Box>
                    </SkeletonGroup>
                  }
                  actions={
                    <Box gap="SP2">
                      <SkeletonGroup skin="light">
                        <SkeletonRectangle height={"36px"} width={"129px"} />
                      </SkeletonGroup>
                      <SkeletonGroup skin="light">
                        <SkeletonRectangle height={"36px"} width={"207px"} />
                      </SkeletonGroup>
                    </Box>
                  }
                />
              )}
            </Box>
          }
          image={
            message._id ? (
              message.image
            ) : (
              <SkeletonGroup skin="light">
                <SkeletonRectangle height={"936px"} width={"100%"} />
              </SkeletonGroup>
            )
          }
        />
      </Card>
    </WdsProviders>
  );
}

export default App;
